import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/shared/AuthContext'
import { useTheme } from '../../context/shared/ThemeContext'
import { Patient } from '../../shared/types/src';

interface CohortAnalysisProps {
  patients: Patient[];
}

interface CohortMetrics {
  totalPatients: number;
  activePatients: number;
  averageEngagement: number;
  commonThemes: Array<{
    theme: string;
    frequency: number;
    percentage: number;
  }>;
  emotionalTrends: Array<{
    emotion: string;
    trend: 'increasing' | 'decreasing' | 'stable';
    percentage: number;
  }>;
  riskLevels: {
    low: number;
    medium: number;
    high: number;
  };
  weeklyActivity: Array<{
    week: string;
    activePatients: number;
    averageSessions: number;
  }>;
}

const CohortAnalysis: React.FC<CohortAnalysisProps> = ({ patients }) => {
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  const [metrics, setMetrics] = useState<CohortMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Generate mock cohort data based on patients
    const generateMockData = () => {
      const totalPatients = patients.length;
      const activePatients = Math.floor(totalPatients * 0.8);
      const averageEngagement = Math.floor(Math.random() * 40) + 60; // 60-100%

      const commonThemes = [
        { theme: 'anxiety_management', frequency: Math.floor(totalPatients * 0.7), percentage: 70 },
        { theme: 'depression_support', frequency: Math.floor(totalPatients * 0.5), percentage: 50 },
        { theme: 'stress_relief', frequency: Math.floor(totalPatients * 0.6), percentage: 60 },
        { theme: 'sleep_improvement', frequency: Math.floor(totalPatients * 0.4), percentage: 40 },
        { theme: 'social_skills', frequency: Math.floor(totalPatients * 0.3), percentage: 30 }
      ];

      const emotionalTrends = [
        { emotion: 'anxiety', trend: 'decreasing' as const, percentage: 65 },
        { emotion: 'depression', trend: 'stable' as const, percentage: 45 },
        { emotion: 'stress', trend: 'increasing' as const, percentage: 55 },
        { emotion: 'happiness', trend: 'increasing' as const, percentage: 70 }
      ];

      const riskLevels = {
        low: Math.floor(totalPatients * 0.5),
        medium: Math.floor(totalPatients * 0.3),
        high: Math.floor(totalPatients * 0.2)
      };

      const weeklyActivity = [
        { week: 'Week 1', activePatients: Math.floor(activePatients * 0.8), averageSessions: 2.1 },
        { week: 'Week 2', activePatients: Math.floor(activePatients * 0.9), averageSessions: 2.3 },
        { week: 'Week 3', activePatients: activePatients, averageSessions: 2.5 },
        { week: 'Week 4', activePatients: Math.floor(activePatients * 0.95), averageSessions: 2.2 }
      ];

      return {
        totalPatients,
        activePatients,
        averageEngagement,
        commonThemes,
        emotionalTrends,
        riskLevels,
        weeklyActivity
      };
    };

    setIsLoading(true);
    setTimeout(() => {
      setMetrics(generateMockData());
      setIsLoading(false);
    }, 1000);
  }, [patients]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return '📈';
      case 'decreasing': return '📉';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-2 text-gray-600">Analyzing cohort data...</span>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8">
        <div className="text-3xl mb-3">📊</div>
        <p className="text-gray-500">No cohort data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className={`text-xl font-semibold ${themeClasses.primaryText} mb-2`}>
          Cohort Analytics
        </h3>
        <p className={`text-sm ${themeClasses.secondaryText}`}>
          Anonymized insights across your patient panel
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${themeClasses.secondaryText}`}>Total Patients</p>
              <p className={`text-2xl font-bold ${themeClasses.primaryText}`}>
                {metrics.totalPatients}
              </p>
            </div>
            <div className="text-3xl">👥</div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${themeClasses.secondaryText}`}>Active Patients</p>
              <p className={`text-2xl font-bold ${themeClasses.primaryText}`}>
                {metrics.activePatients}
              </p>
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${themeClasses.secondaryText}`}>Avg Engagement</p>
              <p className={`text-2xl font-bold ${themeClasses.primaryText}`}>
                {metrics.averageEngagement}%
              </p>
            </div>
            <div className="text-3xl">📊</div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${themeClasses.secondaryText}`}>Avg Sessions/Week</p>
              <p className={`text-2xl font-bold ${themeClasses.primaryText}`}>
                2.3
              </p>
            </div>
            <div className="text-3xl">💬</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Common Themes */}
        <div className={`p-6 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>
            Common Emotional Themes
          </h4>
          <div className="space-y-3">
            {metrics.commonThemes.map((theme, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🎯</span>
                  <span className={`text-sm ${themeClasses.primaryText} capitalize`}>
                    {theme.theme.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${theme.percentage}%` }}
                    ></div>
                  </div>
                  <span className={`text-sm font-medium ${themeClasses.primaryText}`}>
                    {theme.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Level Distribution */}
        <div className={`p-6 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
          <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>
            Risk Level Distribution
          </h4>
          <div className="space-y-3">
            {Object.entries(metrics.riskLevels).map(([level, count]) => (
              <div key={level} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(level)}`}>
                    {level.charAt(0).toUpperCase() + level.slice(1)} Risk
                  </span>
                </div>
                <span className={`text-sm font-medium ${themeClasses.primaryText}`}>
                  {count} patients
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Emotional Trends */}
      <div className={`p-6 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
        <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>
          Emotional Trends
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.emotionalTrends.map((trend, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{getTrendIcon(trend.trend)}</span>
                <span className={`text-sm ${themeClasses.primaryText} capitalize`}>
                  {trend.emotion.replace('_', ' ')}
                </span>
              </div>
              <span className={`text-sm font-medium ${themeClasses.primaryText}`}>
                {trend.percentage}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Activity Chart */}
      <div className={`p-6 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
        <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>
          Weekly Activity Trends
        </h4>
        <div className="grid grid-cols-4 gap-2">
          {metrics.weeklyActivity.map((week, index) => (
            <div key={index} className="text-center">
              <div className={`text-xs ${themeClasses.secondaryText} mb-1`}>
                {week.week}
              </div>
              <div className={`text-lg font-medium ${themeClasses.primaryText} mb-1`}>
                {week.activePatients}
              </div>
              <div className={`text-xs ${themeClasses.secondaryText}`}>
                {week.averageSessions} sessions
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start space-x-2">
          <div className="text-blue-600 text-lg">🔒</div>
          <div className="text-sm text-blue-800">
            <strong>Privacy Notice:</strong> This dashboard shows only anonymized, aggregated data. 
            No individual patient information, names, or identifiable details are displayed. 
            All data is presented in summary form to protect patient privacy.
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortAnalysis; 